import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  FormControlLabel,
  Stack,
  TextField,
  Typography,
} from "@mui/material";

import useData from "../../hooks/useData";

import Page from "./Page";
import Card from "../layout/Card";

const CLIENT_ENTITY_OPTIONS = [
  { key: "skus", label: "SKUs" },
  { key: "listings", label: "Listings" },
  { key: "pricing", label: "Pricing" },
];

const ClientRefresh = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { executeAction, getFromServer } = useData();

  const [clientId, setClientId] = useState(location.state?.clientId || null);
  const [client, setClient] = useState(null);
  const [selectedEntities, setSelectedEntities] = useState([]);
  const [preview, setPreview] = useState(null);
  const [reason, setReason] = useState("");

  const [isLoadingClient, setIsLoadingClient] = useState(true);
  const [isPreviewing, setIsPreviewing] = useState(false);
  const [isApplying, setIsApplying] = useState(false);

  useEffect(() => {
    if (!clientId) {
      setIsLoadingClient(false);
      return;
    }

    const loadClient = async () => {
      setIsLoadingClient(true);
      const results = await getFromServer({
        entityName: "Client",
        id: clientId,
      });
      setClient(results || null);
      setIsLoadingClient(false);
    };

    loadClient();
  }, [clientId]);

  useEffect(() => {
    setPreview(null);
  }, [selectedEntities]);

  const toggleEntity = (key) => {
    setSelectedEntities((current) => {
      if (current.includes(key)) {
        return current.filter((item) => item !== key);
      }
      return [...current, key];
    });
  };

  const handlePreview = async () => {
    if (!clientId) return;

    setIsPreviewing(true);

    const results = await executeAction({
      entityName: "SyncRefreshAudit",
      action: "previewClientRefresh",
      data: {
        clientId,
        selectedEntities,
      },
      displayMessage: true,
    });

    if (results?.isSuccessful) {
      setPreview(results.data || null);
    } else {
      setPreview(null);
    }

    setIsPreviewing(false);
  };

  const handleApply = async () => {
    if (!preview?.audit?.id) return;
    if (!reason.trim()) return;

    setIsApplying(true);

    const results = await executeAction({
      entityName: "SyncRefreshAudit",
      action: "applyRefresh",
      data: {
        auditId: preview.audit.id,
        reason: reason.trim(),
      },
      displayMessage: true,
    });

    if (results?.isSuccessful) {
      setPreview((current) => {
        if (!current) return current;
        return {
          ...current,
          audit: results.data?.audit || current.audit,
        };
      });
    }

    setIsApplying(false);
  };

  const canPreview = !!clientId && selectedEntities.length > 0 && !isPreviewing;
  const canApply = !!preview?.audit?.id && !!reason.trim() && !isApplying;

  return (
    <Page
      pageName="Client Refresh"
      entityName={client?.name || "Client"}
      includeToolbar
      items={[{ name: "back", iconName: "ArrowCircleLeft" }]}
      onToolItemClick={(name) => {
        if (name === "back") {
          navigate("/client", { state: { clientId } });
        }
      }}
    >
      <Box
        display="flex"
        gap={2}
        flexDirection="column"
      >
        <Card title="Scope">
          {isLoadingClient ? (
            <Box
              p={2}
              display="flex"
              alignItems="center"
              gap={1}
            >
              <CircularProgress size={16} />
              <Typography variant="body2">Loading client...</Typography>
            </Box>
          ) : (
            <Box p={2}>
              <Typography variant="body2">
                Client: {client?.name || "Unknown"}
              </Typography>
            </Box>
          )}
        </Card>

        <Card title="Entities to Refresh">
          <Stack
            spacing={0}
            p={2}
          >
            {CLIENT_ENTITY_OPTIONS.map((option) => (
              <FormControlLabel
                key={option.key}
                control={
                  <Checkbox
                    checked={selectedEntities.includes(option.key)}
                    onChange={() => toggleEntity(option.key)}
                  />
                }
                label={option.label}
              />
            ))}
          </Stack>
        </Card>

        <Card title="Preview">
          <Box
            p={2}
            display="flex"
            gap={2}
            flexDirection="column"
          >
            <Box>
              <Button
                variant="contained"
                disabled={!canPreview}
                onClick={handlePreview}
              >
                {isPreviewing ? "Previewing..." : "Preview Counts"}
              </Button>
            </Box>

            {preview?.warningShown ? (
              <Alert severity="warning">{preview.warningMessage}</Alert>
            ) : null}

            {preview?.counts ? (
              <Box>
                {Object.entries(preview.counts).map(([key, value]) => (
                  <Typography
                    key={key}
                    variant="body2"
                  >
                    {key}: {value}
                  </Typography>
                ))}
                <Typography
                  variant="subtitle2"
                  sx={{ mt: 1 }}
                >
                  Total Records: {preview.previewTotalCount || 0}
                </Typography>
              </Box>
            ) : null}
          </Box>
        </Card>

        <Card title="Apply Refresh">
          <Box
            p={2}
            display="flex"
            flexDirection="column"
            gap={2}
          >
            <TextField
              label="Reason"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              size="small"
              required
              multiline
              minRows={2}
            />

            <Box>
              <Button
                variant="contained"
                disabled={!canApply}
                onClick={handleApply}
              >
                {isApplying ? "Queueing..." : "Apply Refresh"}
              </Button>
            </Box>

            {preview?.audit?.status ? (
              <Typography variant="body2">
                Status: {preview.audit.status}
              </Typography>
            ) : null}
          </Box>
        </Card>
      </Box>
    </Page>
  );
};

export default ClientRefresh;
